'use strict';

/**
 * Helper: Date Time
 *
 * NOTE: When making changes to this file you must create an ES5 version prior to release.
 *
 * @version
 * 12.12.3 MLIBJS-149 Add method for formatting fab time as HH:MM:SS
 * 12.4.0 #756 MLIBJS-121 Add validation to export DateTimeHelper for ES6 usage
 * 7.17.0 #564 Update `getDateAsString` to take an optional date format
 * 7.16.0 #557 Add missing `wdDateTimeHelper` functionality
 * 7.10.0 #522 Add time conversion functionality
 *
 * @author Dallas Vogels <dvogels@webduct.com>
 * @author Ryan Sweeney <rsweeney@webduct.com>
 * @author Erik Orozco <eorozco@intersog.com>
 * @author Deitric Jang <djang@buildcentrix.com>
 *
 * @copyright (c) 2017-2022 Build Centrix
 */
export default class DateTimeHelper {

    /**
     * Constructor
     *
     * Note that any functions in the constructor are considered private.
     * @see http://stackoverflow.com/a/28165599/1304537
     *
     * @since 7.10.0
     *
     * @param {DateTimeHelperMomentAdapter} dateTimeLibraryAdapter
     */
    constructor(dateTimeLibraryAdapter) {

        this.dateTimeLibraryAdapter = dateTimeLibraryAdapter;

        this.setDateFormat('YYYY-MM-DD'); // default
    }

    /**
     * Add Days (+/-) to Date
     * @since 7.10.0
     *
     * @param {Date} dateToFormat
     * @param {String} key
     * @param {Number} value
     * @returns {Date|null}
     */
    addTime(dateToFormat, key, value) {

        return this.dateTimeLibraryAdapter.addTime(dateToFormat, key, value);

    };

    /**
     * Format String Date as JSDate
     *
     * @since 7.10.0
     *
     * @param {String} dateToFormat
     *
     * @returns {Date|null}
     */
    formatDate(dateToFormat) {

        return this.dateTimeLibraryAdapter.formatDate(dateToFormat);

    };

    /**
     * Get Date as String
     *
     * @since 7.17.0
     *
     * @param {Date} dateToFormat
     * @param {String} format
     * @returns {String|null}
     */
    getDateAsString(dateToFormat, format = null) {
        
        return this.dateTimeLibraryAdapter.getDateAsString(dateToFormat, format);

    };

    /**
     * Get Date Format
     *
     * @since 7.10.0
     *
     * @returns {string}
     */
    getDateFormat() {
        return this.dateTimeLibraryAdapter.getDateFormat();
    };

    /**
     * Get the start date with minutes
     *
     * @since 7.10.0
     *
     * @param {Date} date
     * @returns {Date} Returns the date parameter with the time component set to 00:00:00
     */
    getDateStartWithMinutes(date) {
        return new Date(date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate() + ' 00:00:00');
    }

    /**
     * Get Duration Between Dates as Number of Days
     *
     * @since 7.10.0
     *
     * @param {Date} startDate
     * @param {Date} endDate
     * @returns {Number}
     */
    getDurationAsDays(startDate, endDate) {
        return this.dateTimeLibraryAdapter.getDurationAsDays(startDate, endDate);
    };

    /**
     * Get Start of Current Month
     *
     * @since 7.16.0
     *
     * @param {Date} date
     * @returns {Date}
     */
    getEndOfMonth(date) {
        return this.dateTimeLibraryAdapter.getEndOfMonth(date);
    };

    /**
     * Get Todays Date
     *
     * @since 7.10.0
     *
     * @returns {Date}
     */
    getToday() {
        return this.dateTimeLibraryAdapter.getToday();
    };

    /**
     * Get Start of Current Month
     *
     * @since 7.16.0
     *
     * @param {Date} date
     * @returns {Date}
     */
    getStartOfMonth(date) {
        return this.dateTimeLibraryAdapter.getStartOfMonth(date);
    };

    /**
     * Get Time From Now
     *
     * @since 7.16.0
     *
     * @param {Date} date
     * @returns {String}
     */
    getTimeFromNow(date) {
        return this.dateTimeLibraryAdapter.getTimeFromNow(date);
    };

    /**
     * Check if target date is before the match date
     *
     * @since 7.16.0
     *
     * @param {Date} dateToCheck
     * @param {Date} dateToMatch
     * @returns {Boolean}
     */
    isBefore(dateToCheck, dateToMatch) {

        return this.dateTimeLibraryAdapter.isBefore(dateToCheck, dateToMatch);

    };
    
    /**
     * Check if target date is after the match date
     *
     * @since 7.10.0
     *
     * @param {Date} dateToCheck
     * @param {Date} dateToMatch
     * @returns {Boolean}
     */
    isAfter(dateToCheck, dateToMatch) {

        return this.dateTimeLibraryAdapter.isAfter(dateToCheck, dateToMatch);

    };

    /**
     * Is the Target Date within the Date Range?
     *
     * @since 7.10.0
     *
     * @param {Date} startDate
     * @param {Date} endDate
     * @param {Date} targetDate
     * @returns {Boolean}
     */
    isDateWithinDateRange(startDate, endDate, targetDate) {

        return this.dateTimeLibraryAdapter.isDateWithinDateRange(startDate, endDate, targetDate)

    };

    /**
     * Check if date is within the specified minutes
     *
     * Note that the end date must be in the future.
     *
     * @since 7.10.0
     *
     * @param {Date} startDate
     * @param {Date} endDate
     * @param {Number} minutes
     * @return {Boolean}
     */
    isDateWithinMinutes(startDate, endDate, minutes) {

        if (endDate.getTime() < startDate.getTime()) {
            throw new Error('endDate must be >= to startDate');
        }

        let result = false;

        let timeInMilliseconds = minutes * 60 * 1000;
        let dateToCheck = startDate.getTime() + timeInMilliseconds;

        if (endDate.getTime() <= dateToCheck) {
            result = true;
        }

        return result;

    }

    /**
     * Check if date/time is rounded by specified minutes
     *
     * @since 7.10.0
     *
     * @param {Date} date
     * @param {Number} minutes
     * @return {Boolean}
     */
    isRoundedByMinutes(date, minutes) {

        let result = false;

        let roundedDate = this.roundByMinutes(date, minutes);

        if (roundedDate.getTime() === date.getTime()) {
            result = true;
        }

        return result;
    }


    /**
     * Are Two Dates the Same
     * @since 7.10.0
     *
     * @param {Date} dateToCheck
     * @param {Date} dateToMatch
     * @returns {Boolean}
     */
    isSameDate(dateToCheck, dateToMatch) {

        return this.dateTimeLibraryAdapter.isSameDate(dateToCheck, dateToMatch);

    };

    /**
     * Check if target date is the same or after the match date
     *
     * @since 7.10.0
     *
     * @param {Date} dateToCheck
     * @param {Date} dateToMatch
     * @returns {Boolean}
     */
    isSameOrAfter(dateToCheck, dateToMatch) {

        return this.dateTimeLibraryAdapter.isSameOrAfter(dateToCheck, dateToMatch);

    };

    /**
     * Check if target date is the same or before the match date
     *
     * @since 7.10.0
     *
     * @param {Date} dateToCheck
     * @param {Date} dateToMatch
     * @returns {Boolean}
     */
    isSameOrBefore(dateToCheck, dateToMatch) {

        return this.dateTimeLibraryAdapter.isSameOrBefore(dateToCheck, dateToMatch);

    };

    /**
     * Round date/time by specified minutes
     *
     * @since 7.10.0
     *
     * @see http://stackoverflow.com/a/10789415/1304537
     *
     * Examples:
     *
     * Using 5 minutes would round to 0,5,10,15,20,25,...
     *
     * Using 6 minutes would round to 0,6,12,18,24,30,...
     *
     * @param {Date} date
     * @param {Number} minutes
     * @returns {Date}
     */
    roundByMinutes(date, minutes) {

        let coEfficient = 1000 * 60 * minutes;

        return new Date(Math.round(date.getTime() / coEfficient) * coEfficient);

    }

    /**
     * Set Date Format
     *
     * @since 7.10.0
     *
     * @param {string} format
     */
    setDateFormat(format) {
        this.dateTimeLibraryAdapter.setDateFormat(format);
    };

    /**
     * Given two date ranges check if date range #1 contains date range #2.
     *
     * @since 7.10.0
     *
     * @param {Date} dateStarted Date started of container range
     * @param {Date} dateEnded Date ended of container range
     * @param {Date} targetDateStarted Date started of contained range
     * @param {Date} targetDateEnded Date ended of contained range
     *
     * @return {Boolean}
     */
    containsDateDuration(dateStarted, dateEnded, targetDateStarted, targetDateEnded) {
        return this.dateTimeLibraryAdapter.containsDateDuration(dateStarted, dateEnded, targetDateStarted, targetDateEnded);
    };

    /**
     * Check if a start date intersects a date range.
     *
     * @since 7.10.0
     *
     * @param {Date} dateStarted The start date to check
     * @param {Date} targetDateStarted The start date of the target date range
     * @param {Date} targetDateEnded The end date of the target date range
     * @param {Boolean} ignoreExactMatch If true and dateStarted is the same as the targetDateEnded then result returned is false (i.e no intersect)
     *
     * @returns {Boolean}
     */
    startDateIntersects(dateStarted, targetDateStarted, targetDateEnded, ignoreExactMatch = false) {
        return this.dateTimeLibraryAdapter.startDateIntersects(dateStarted, targetDateStarted, targetDateEnded, ignoreExactMatch);
    }

    /**
     * Check if an end date intersects a date range.
     *
     * @since 7.10.0
     *
     * @param {Date} dateEnded The end date to check
     * @param {Date} targetDateStarted The start date of the target date range
     * @param {Date} targetDateEnded The end date of the target date range
     * @param {Boolean} ignoreExactMatch If true and dateEnded is the same as the targetDateStarted then result returned is false (i.e no intersect)
     *
     * @returns {Boolean}
     */
    endDateIntersects(dateEnded, targetDateStarted, targetDateEnded, ignoreExactMatch = false) {
        return this.dateTimeLibraryAdapter.endDateIntersects(dateEnded, targetDateStarted, targetDateEnded, ignoreExactMatch);
    }

    /**
     * Check if a date intersects a date range
     *
     * @since 7.10.0
     *
     * @param {Date} intersectDate Date to check for intersection
     * @param {Date} compareDateStarted Start date of date range to check
     * @param {Date} compareDateEnded End date of date range to check
     *
     * @returns {Boolean}
     */
    dateIntersects(intersectDate, compareDateStarted, compareDateEnded) {
        return this.dateTimeLibraryAdapter.dateIntersects(intersectDate, compareDateStarted, compareDateEnded);
    }

    /**
     * Check if a date range (targetDateStarted, targetDateEnded) envelops another date range (dateStarted, dateEnded)
     *
     * @since 7.10.0
     *
     * @param {Date} dateStarted The start date of the range to check if it is enveloped by the target range
     * @param {Date} dateEnded The end date of the range to check if it is enveloped by the target range
     * @param {Date} targetDateStarted The start date of the range to check if it envelopes
     * @param {Date} targetDateEnded The end date of the range to check if it envelopes
     *
     * @returns {Boolean}
     */
    envelops(dateStarted, dateEnded, targetDateStarted, targetDateEnded) {
        return this.dateTimeLibraryAdapter.envelops(dateStarted, dateEnded, targetDateStarted, targetDateEnded);
    }

    /**
     * Check if a date range (targetDateStarted, targetDateEnded) is enveloped by another date range (dateStarted, dateEnded)
     *
     * @since 7.10.0
     *
     * @param {Date} dateStarted The start date of the range to check if it envelops the target range
     * @param {Date} dateEnded The end date of the range to check if it envelops the target range
     * @param {Date} targetDateStarted The start date of the range to check if it is being enveloped
     * @param {Date} targetDateEnded The end date of the range to check if it is being enveloped
     *
     * @returns {Boolean}
     */
    enveloped(dateStarted, dateEnded, targetDateStarted, targetDateEnded) {
        return this.dateTimeLibraryAdapter.enveloped(dateStarted, dateEnded, targetDateStarted, targetDateEnded);
    }

    /**
     * Humanize Duration
     *
     * @since 7.10.0
     *
     * @param {Number} input seconds
     * @param {String} inputUnit can be one: 'y', 'M', 'w', 'd', 'h', 'm', 's', 'ms'
     * @param {Array|String} outputUnits e.g. ['y', 'M', 'w', 'd', 'h', 'm', 's', 'ms'], 'm'
     * @param {Boolean} round default false
     *
     * @returns {String}
     */
    humanizeDuration(input, inputUnit, outputUnits, round = false) {
        return this.dateTimeLibraryAdapter.humanizeDuration(input, inputUnit, outputUnits, round);
    }

    /**
     * Format the input (hours) to format: hhh:mm:ss
     *
     * @since 12.12.3
     *
     * @param {Number} timeInHours A float value representing the number of hours
     * @param {String} locale
     * @returns {String} For example an input of 0.095 will return '0:05:42' (5 minutes, 42 seconds)
     */
    formatDecimalHourInHourMinute(timeInHours, locale = 'en-US') {
        const hours = Math.floor(timeInHours);
        let minutes = Math.floor(60 * (timeInHours - hours));
        let seconds = (timeInHours * 3600) % 60;

        const localeOptions = {
            minimumIntegerDigits: 2,
            useGrouping:false
        };

        minutes = minutes.toLocaleString(locale, localeOptions);
        seconds = seconds.toLocaleString(locale, localeOptions);

        return `${hours}:${minutes}:${seconds}`;
    }

}

// CommonJS: this must be after the class declaration
if (typeof module !== 'undefined' && module.exports) {
    module.exports = DateTimeHelper;
}

// EOF